/**
 * BUSINESS API — Wrapper dei REST Liferay allineato a BusinessesResourceImpl.java.
 * I 5 metodi CRUD standard vengono da `createCrudApi`; sopra si aggiunge la
 * `resolve` anti-duplicato.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /businesses/{groupId}   (+ page, pageSize, sort, search, kanbanId)  -> Page<Business>
 *   - GET    /businesses/{groupId}/resolve  (name, domain, emailAddress)          -> Business | 404
 *   - POST   /businesses/{groupId}  BusinessInput                                -> Business
 *   - GET    /business/{businessId}                                               -> Business
 *   - PATCH  /business/{businessId} BusinessInput (parziale)                     -> Business
 *   - DELETE /business/{businessId}                                               -> 200
 *
 * Sub-resources (emailAddress, phone, address) in file dedicati.
 * `businessUpdateToInput` ignora `businessId` (whitelist).
 */

import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  businessFromDto,
  businessCreateToInput,
  businessUpdateToInput,
  type BusinessDto,
  type Business,
} from './businessMappers';
import type { BusinessCreatePayload, BusinessListParams, BusinessUpdatePayload } from './businessTypes';

const COLLECTION = '/businesses';

/**
 * Dati di contatto con cui chiedere al backend se un'azienda è già a sistema.
 * Tutti opzionali: il backend prova i candidati che trova, in cascata.
 */
export interface BusinessResolveParams {
  name?: string;
  domain?: string;
  emailAddress?: string;
}

const base = createCrudApi<Business, BusinessDto, BusinessCreatePayload, BusinessUpdatePayload, BusinessListParams>({
  collection: COLLECTION,
  single: '/business',
  fromDto: businessFromDto,
  createToInput: businessCreateToInput,
  updateId: (p) => p.businessId,
  updateToInput: (p) => businessUpdateToInput(p),
});

export const businessApi = {
  ...base,

  /**
   * L'azienda già censita che corrisponde ai dati di contatto, oppure `null`.
   * La regola (dominio → dominio email → nome, in forma canonica) vive nel
   * backend (stessa degli extractor): qui non si decide nulla, si chiede. Il 404
   * non è un errore ma "nessuna corrispondenza" → `null`, e copre anche il caso
   * "azienda esistente ma non visibile all'utente".
   */
  resolve: (groupId: number, params: BusinessResolveParams): Promise<Business | null> =>
    httpClient
      .get<BusinessDto>(`${COLLECTION}/${groupId}/resolve`, {
        params: params as Record<string, unknown>,
      })
      .then((r) => businessFromDto(r.data))
      .catch((err: unknown) => {
        if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
        throw err;
      }),
};
